import { CopyIcon } from 'app/components/Icons';
import {
  TRegisterBusinessDataRequest,
  TRegisterPersonDataRequest,
} from 'modules/customer/context';
import {
  useCustomerService,
  usePersonService,
} from 'modules/customer/services';
import { TProducts } from 'modules/products/context';
import {
  useApplicationsService,
  useQualificationsService,
} from 'modules/products/services/hooks';
import { TBorrowerQualificationRequestType } from 'modules/products/services/hooks/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { ButtonPrimary, Spinner, TextM } from 'app/components';
import { ProductSideSheet } from 'modules/products/components/ProductSideSheet';
import { EmployerContent, ConditionContent } from './components';
import { TApplicationData } from '../NaturalPersonViewApplicationTab/NaturalPersonViewApplicationTab';
import { ProductContent } from './components/ProductContent';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { useTheme } from '@mui/material';

export interface IApplicationDetails {
  handleClose: () => void;
  isOpen: boolean;
  application: TApplicationData | null;
  handleUpdateQualificationStatus: (
    application: TApplicationData | null,
    identifier: string,
  ) => void;
}

const NaturalPersonViewApplicationSideSheet = ({
  handleClose,
  handleUpdateQualificationStatus,
  isOpen,
  application,
}: IApplicationDetails) => {
  const theme = useTheme();
  const { loadings } = useApplicationsService();
  const { getEmployerQualification } = useQualificationsService();
  const { getPerson } = usePersonService();
  const { getBusiness } = useCustomerService();
  const [borrowerPersonData, setBorrowerPersonData] =
    useState<TRegisterPersonDataRequest>();
  const [borrowerBusinessData, setBorrowerBusinessData] =
    useState<TRegisterBusinessDataRequest>();
  const [borrowerQualificationData, setBorrowerQualificationData] = useState<
    TBorrowerQualificationRequestType[] | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbarCopy } = useSnackbar();

  const onClose = () => {
    handleClose();
  };

  const fetchBorrowerQualifications = useCallback(async () => {
    setIsLoading(true);
    try {
      if (application) {
        const { productData } = application;
        if (typeof productData?.name !== 'string') {
          return;
        }
        if (productData) {
          await handleGetBorrower(productData, application?.borrower.id || '');
        }
        const borrowerQualificationRes = await handleGetBorrowerQualification(
          productData?.id || '',
          application?.borrower.id || '',
        );
        if (!borrowerQualificationRes) {
          handleClose();
        }
        setBorrowerQualificationData(borrowerQualificationRes?.content);
      }
    } finally {
      setIsLoading(false);
    }
  }, [application]);

  const handleGetBorrower = useCallback(
    async (product: TProducts, borrowerId: string) => {
      try {
        if (product.borrower_type === 'PERSON') {
          const personData = await getPerson(borrowerId || '');
          if (!personData) {
            handleClose();
          }
          setBorrowerPersonData(
            personData || ({} as TRegisterPersonDataRequest),
          );
        }
        if (product.borrower_type === 'BUSINESS') {
          const personData = await getBusiness(borrowerId || '');
          if (!personData) {
            handleClose();
          }
          setBorrowerBusinessData(
            personData || ({} as TRegisterBusinessDataRequest),
          );
        }
      } catch (error) {
        handleClose();
      }
    },
    [application],
  );

  const handleGetBorrowerQualification = useCallback(
    async (productId: string, borrowerId: string) => {
      try {
        const borrowerQualificationRes = await getEmployerQualification(
          productId,
          borrowerId,
        );
        return borrowerQualificationRes;
      } catch (error) {
        handleClose();
      }
    },
    [],
  );

  const handleCopyId = useCallback(() => {
    navigator.clipboard.writeText(`${application?.id || ''}`);
    showSnackbarCopy();
  }, [application]);

  useEffect(() => {
    if (isOpen) fetchBorrowerQualifications();
  }, [isOpen]);

  return (
    <ProductSideSheet
      open={isOpen}
      handleClose={onClose}
      title={'Solicitação'}
      subtitleItem={
        isLoading ? null : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <TextM>ID: {application?.id || '-'}</TextM>
            <ButtonPrimary
              typeVariant="outline"
              style={{ padding: '10px 15px', width: 'fit-content' }}
              onClick={handleCopyId}
            >
              <CopyIcon color={theme.palette.brand.primary.base as string} />
            </ButtonPrimary>
          </div>
        )
      }
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <ConditionContent
            applicationData={application}
            handleUpdateQualificationStatus={handleUpdateQualificationStatus}
            isStatusOptionsLoading={
              loadings.isQualificationStatusOptionsLoading
            }
          />
          {typeof application?.productData?.name === 'string' && (
            <>
              <EmployerContent
                borrowerPerson={borrowerPersonData}
                borrowerBusiness={borrowerBusinessData}
                applicationData={application}
                borrowerQualificationData={borrowerQualificationData?.[0]}
              />
              <ProductContent applicationData={application} />
            </>
          )}
        </>
      )}
    </ProductSideSheet>
  );
};

export default NaturalPersonViewApplicationSideSheet;
